import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../img/logo.png';
import { Shield } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

interface LegalPageTemplateProps {
    title: string;
    children: ReactNode;
    lastUpdated?: string;
}

const LegalPageTemplate: React.FC<LegalPageTemplateProps> = ({
                                                                 title,
                                                                 children,
                                                                 lastUpdated = "1 avril 2025"
                                                             }) => {
    const { t, isRTL } = useLanguage();

    return (
        <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Navigation simplifiée */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex-shrink-0 flex items-center">
                            <Link to="/">
                                <img src={logo} alt="Inspecly Logo" width={80} height={80} />
                            </Link>
                        </div>
                        <div className="flex gap-4">
                            <Link to="/" className="text-gray-600 hover:text-[#115DF2]">
                                {t('legalTemplate.backToHome')}
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Contenu principal */}
            <div className="py-10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow overflow-hidden rounded-lg">
                        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                            <h1 className={`text-2xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {title}
                            </h1>
                            <p className={`mt-1 text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('legalTemplate.lastUpdated')} {lastUpdated}
                            </p>
                        </div>
                        <div className="px-4 py-5 sm:p-6 prose max-w-none">
                            {children}
                        </div>
                    </div>

                    {/* Liens vers les autres documents légaux */}
                    <div className="mt-8 bg-white shadow overflow-hidden rounded-lg">
                        <div className="px-4 py-5 sm:px-6">
                            <h2 className={`text-lg font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('legalTemplate.otherDocuments.title')}
                            </h2>
                        </div>
                        <div className="border-t border-gray-200">
                            <div className="px-4 py-4 sm:px-6">
                                <ul className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <li>
                                        <Link to="/cgu" className="text-[#115DF2] hover:underline">
                                            {t('legalTemplate.otherDocuments.termsOfUse')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/cgv" className="text-[#115DF2] hover:underline">
                                            {t('legalTemplate.otherDocuments.termsOfSale')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/privacy-policy" className="text-[#115DF2] hover:underline">
                                            {t('legalTemplate.otherDocuments.privacyPolicy')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/cookie-policy" className="text-[#115DF2] hover:underline">
                                            {t('legalTemplate.otherDocuments.cookiePolicy')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer simplifié */}
            <footer className="bg-[#0A337F] text-white mt-8">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="flex items-center gap-2 mb-4 md:mb-0">
                            <Shield className="h-6 w-6" />
                            <span className="text-xl font-bold">
                                Inspecly<span className="text-[#9DB5F3] font-normal">.io</span>
                            </span>
                        </div>
                        <div>
                            <p className="text-sm text-[#C5D3F9]">
                                &copy; {new Date().getFullYear()} Inspecly.io. {t('legalTemplate.footer.rights')}
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LegalPageTemplate;